import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Stack, FormControl, InputLabel, Select, MenuItem,
  Checkbox, ListItemText, CssBaseline, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import dataStore from '../dataStore';
import { useLogoMaps } from '../useLogoMaps';

// ══════════════════════════════════════════════════════════
//  SURVIVOR GRID
//
//  Rows: every NFL team. Columns: remaining contest legs (past
//  weeks drop off automatically). Cells: opponent + win prob from
//  Caesars lookahead moneylines (sole full-season source), with the
//  greedy
//  optimal path (one pick per leg, each team used once, restricted
//  to teams the user still has available) is highlighted, and its
//  cumulative advance probability is shown as Survival Rate.
// ══════════════════════════════════════════════════════════

const THROTTLE_MS = 5000; // lookahead lines move slowly
// Books are tried in order; the first one quoting BOTH sides wins. If none of
// the preferred books has a two-sided price we fall back to the median de-vigged
// probability across every book that does. Caesars is first because it posts the
// furthest out, but it drops games (e.g. SF/ARI), so it is a preference, not a
// requirement.
const BOOK_PREFERENCE = ['Caesars', 'FanDuel', 'DraftKings', 'BetMGM', 'ESPN BET'];
const MIN_CONSENSUS_BOOKS = 1; // raise to 2+ to suppress lone-book outliers
// Books only price NFL games about two weeks out. Further ahead they still
// quote, but those are placeholders that de-vig to ~50% whoever is playing.
// Beyond this horizon we ignore the feed entirely and use the preseason board
// plus the revision fit, which is strictly better informed.
const MARKET_HORIZON_DAYS = 14;

// ── Preseason lookahead board (Caesars + consensus, captured pre-season) ──
// [week, home, away, P(home wins) de-vigged, kickoff ISO]. All 272 games,
// every week, neutral sites and international games already reflected in the
// probability. This is the baseline the model revises; it is also the only
// source of the full-season schedule, since the odds feed carries a game only
// once a book has priced it.
const PRESEASON_BOARD = [
  [1,"SEA","NE",0.6357,"2026-09-10T00:20:00Z"], [1,"LAR","SF",0.6407,"2026-09-11T00:35:00Z"],
  [1,"CIN","TB",0.653,"2026-09-13T17:00:00Z"], [1,"IND","BAL",0.3705,"2026-09-13T17:00:00Z"],
  [1,"PIT","ATL",0.6092,"2026-09-13T17:00:00Z"], [1,"JAX","CLE",0.7822,"2026-09-13T17:00:00Z"],
  [1,"HOU","BUF",0.4925,"2026-09-13T17:00:00Z"], [1,"CAR","CHI",0.4226,"2026-09-13T17:00:00Z"],
  [1,"TEN","NYJ",0.5722,"2026-09-13T17:00:00Z"], [1,"DET","NO",0.7515,"2026-09-13T17:00:00Z"],
  [1,"PHI","WAS",0.671,"2026-09-13T20:25:00Z"], [1,"LV","MIA",0.6447,"2026-09-13T20:25:00Z"],
  [1,"MIN","GB",0.5194,"2026-09-13T20:25:00Z"], [1,"LAC","ARI",0.8403,"2026-09-13T20:25:00Z"],
  [1,"NYG","DAL",0.4199,"2026-09-14T00:20:00Z"], [1,"KC","DEN",0.5763,"2026-09-15T00:15:00Z"],
  [2,"BUF","DET",0.5976,"2026-09-18T00:15:00Z"], [2,"HOU","CIN",0.5629,"2026-09-20T17:00:00Z"],
  [2,"NYJ","GB",0.3051,"2026-09-20T17:00:00Z"], [2,"TEN","PHI",0.3386,"2026-09-20T17:00:00Z"],
  [2,"NE","PIT",0.6936,"2026-09-20T17:00:00Z"], [2,"BAL","NO",0.7688,"2026-09-20T17:00:00Z"],
  [2,"TB","CLE",0.7152,"2026-09-20T17:00:00Z"], [2,"CHI","MIN",0.6463,"2026-09-20T17:00:00Z"],
  [2,"ATL","CAR",0.5354,"2026-09-20T17:00:00Z"], [2,"LAC","LV",0.7875,"2026-09-20T20:05:00Z"],
  [2,"DEN","JAX",0.5862,"2026-09-20T20:05:00Z"], [2,"SF","MIA",0.845,"2026-09-20T20:25:00Z"],
  [2,"DAL","WAS",0.6463,"2026-09-20T20:25:00Z"], [2,"ARI","SEA",0.1772,"2026-09-20T20:25:00Z"],
  [2,"KC","IND",0.7374,"2026-09-21T00:20:00Z"], [2,"LAR","NYG",0.7888,"2026-09-22T00:15:00Z"],
  [3,"GB","ATL",0.7671,"2026-09-25T00:15:00Z"], [3,"NYG","TEN",0.6151,"2026-09-27T17:00:00Z"],
  [3,"PIT","CIN",0.4715,"2026-09-27T17:00:00Z"], [3,"IND","HOU",0.4771,"2026-09-27T17:00:00Z"],
  [3,"CLE","CAR",0.443,"2026-09-27T17:00:00Z"], [3,"BUF","LAC",0.6079,"2026-09-27T17:00:00Z"],
  [3,"DET","NYJ",0.8064,"2026-09-27T17:00:00Z"], [3,"JAX","NE",0.5266,"2026-09-27T17:00:00Z"],
  [3,"MIA","KC",0.2263,"2026-09-27T17:00:00Z"], [3,"WAS","SEA",0.3693,"2026-09-27T17:00:00Z"],
  [3,"TB","MIN",0.5499,"2026-09-27T20:05:00Z"], [3,"SF","ARI",0.8466,"2026-09-27T20:05:00Z"],
  [3,"DAL","BAL",0.4433,"2026-09-27T20:25:00Z"], [3,"NO","LV",0.6407,"2026-09-27T20:25:00Z"],
  [3,"DEN","LAR",0.4034,"2026-09-28T00:20:00Z"], [3,"CHI","PHI",0.5266,"2026-09-29T00:15:00Z"],
  [4,"CLE","PIT",0.4202,"2026-10-02T00:15:00Z"], [4,"WAS","IND",0.4936,"2026-10-04T13:30:00Z"],
  [4,"CHI","NYJ",0.7924,"2026-10-04T17:00:00Z"], [4,"HOU","DAL",0.5776,"2026-10-04T17:00:00Z"],
  [4,"TB","GB",0.4771,"2026-10-04T17:00:00Z"], [4,"PHI","LAR",0.4364,"2026-10-04T17:00:00Z"],
  [4,"BAL","TEN",0.789,"2026-10-04T17:00:00Z"], [4,"NYG","ARI",0.7403,"2026-10-04T17:00:00Z"],
  [4,"CIN","JAX",0.5776,"2026-10-04T17:00:00Z"], [4,"BUF","NE",0.6252,"2026-10-04T17:00:00Z"],
  [4,"MIN","MIA",0.782,"2026-10-04T20:05:00Z"], [4,"SEA","LAC",0.6165,"2026-10-04T20:25:00Z"],
  [4,"LV","KC",0.3137,"2026-10-04T20:25:00Z"], [4,"SF","DEN",0.5777,"2026-10-04T20:25:00Z"],
  [4,"CAR","DET",0.3921,"2026-10-05T00:20:00Z"], [4,"NO","ATL",0.5549,"2026-10-06T00:15:00Z"],
  [5,"DAL","TB",0.6443,"2026-10-09T00:15:00Z"], [5,"JAX","PHI",0.4649,"2026-10-11T13:30:00Z"],
  [5,"MIA","CIN",0.2855,"2026-10-11T17:00:00Z"], [5,"NYJ","CLE",0.5601,"2026-10-11T17:00:00Z"],
  [5,"PIT","IND",0.5511,"2026-10-11T17:00:00Z"], [5,"NE","LV",0.7896,"2026-10-11T17:00:00Z"],
  [5,"NO","MIN",0.4939,"2026-10-11T17:00:00Z"], [5,"TEN","HOU",0.3779,"2026-10-11T17:00:00Z"],
  [5,"WAS","NYG",0.5646,"2026-10-11T17:00:00Z"], [5,"LAC","DEN",0.5862,"2026-10-11T20:05:00Z"],
  [5,"GB","CHI",0.6188,"2026-10-11T20:25:00Z"], [5,"SEA","SF",0.6572,"2026-10-11T20:25:00Z"],
  [5,"ARI","DET",0.2205,"2026-10-11T20:25:00Z"], [5,"ATL","BAL",0.3459,"2026-10-12T00:20:00Z"],
  [5,"LAR","BUF",0.6402,"2026-10-13T00:15:00Z"], [6,"DEN","SEA",0.4667,"2026-10-16T00:15:00Z"],
  [6,"JAX","HOU",0.4959,"2026-10-18T13:30:00Z"], [6,"NE","NYJ",0.812,"2026-10-18T17:00:00Z"],
  [6,"NYG","NO",0.5886,"2026-10-18T17:00:00Z"], [6,"ATL","CHI",0.3946,"2026-10-18T17:00:00Z"],
  [6,"PHI","CAR",0.7388,"2026-10-18T17:00:00Z"], [6,"IND","TEN",0.6673,"2026-10-18T17:00:00Z"],
  [6,"CLE","BAL",0.2867,"2026-10-18T17:00:00Z"], [6,"TB","PIT",0.5346,"2026-10-18T17:00:00Z"],
  [6,"LAR","ARI",0.8872,"2026-10-18T20:05:00Z"], [6,"KC","LAC",0.5692,"2026-10-18T20:25:00Z"],
  [6,"LV","BUF",0.264,"2026-10-18T20:25:00Z"], [6,"GB","DAL",0.6222,"2026-10-19T00:20:00Z"],
  [6,"SF","WAS",0.6951,"2026-10-20T00:15:00Z"], [7,"CHI","NE",0.5285,"2026-10-23T00:15:00Z"],
  [7,"NO","PIT",0.4488,"2026-10-25T13:30:00Z"], [7,"MIN","IND",0.5756,"2026-10-25T17:00:00Z"],
  [7,"TEN","CLE",0.6049,"2026-10-25T17:00:00Z"], [7,"NYJ","MIA",0.584,"2026-10-25T17:00:00Z"],
  [7,"CAR","TB",0.4715,"2026-10-25T17:00:00Z"], [7,"ATL","SF",0.3284,"2026-10-25T17:00:00Z"],
  [7,"BAL","CIN",0.6452,"2026-10-25T17:00:00Z"], [7,"HOU","NYG",0.7194,"2026-10-25T17:00:00Z"],
  [7,"ARI","DEN",0.241,"2026-10-25T20:05:00Z"], [7,"LV","LAR",0.2176,"2026-10-25T20:25:00Z"],
  [7,"DET","GB",0.5712,"2026-10-25T20:25:00Z"], [7,"SEA","KC",0.6049,"2026-10-26T00:20:00Z"],
  [7,"PHI","DAL",0.6225,"2026-10-27T00:15:00Z"], [8,"GB","CAR",0.772,"2026-10-30T00:15:00Z"],
  [8,"DET","MIN",0.6929,"2026-11-01T18:00:00Z"], [8,"JAX","IND",0.6572,"2026-11-01T18:00:00Z"],
  [8,"PIT","CLE",0.6973,"2026-11-01T18:00:00Z"], [8,"NYJ","LV",0.5224,"2026-11-01T18:00:00Z"],
  [8,"DAL","ARI",0.8313,"2026-11-01T18:00:00Z"], [8,"TB","ATL",0.6689,"2026-11-01T18:00:00Z"],
  [8,"CIN","TEN",0.759,"2026-11-01T18:00:00Z"], [8,"BUF","BAL",0.5838,"2026-11-01T18:00:00Z"],
  [8,"LAR","LAC",0.6746,"2026-11-01T21:05:00Z"], [8,"MIA","NE",0.2489,"2026-11-01T21:25:00Z"],
  [8,"DEN","KC",0.5346,"2026-11-01T21:25:00Z"], [8,"WAS","PHI",0.4288,"2026-11-02T01:20:00Z"],
  [8,"SEA","CHI",0.6902,"2026-11-03T01:15:00Z"], [9,"BAL","JAX",0.6689,"2026-11-06T01:15:00Z"],
  [9,"ATL","CIN",0.3369,"2026-11-08T14:30:00Z"], [9,"MIA","DET",0.2706,"2026-11-08T18:00:00Z"],
  [9,"NO","CLE",0.669,"2026-11-08T18:00:00Z"], [9,"PHI","NYG",0.7,"2026-11-08T18:00:00Z"],
  [9,"IND","DAL",0.5,"2026-11-08T18:00:00Z"], [9,"CAR","DEN",0.4253,"2026-11-08T18:00:00Z"],
  [9,"KC","NYJ",0.8236,"2026-11-08T18:00:00Z"], [9,"WAS","LAR",0.3254,"2026-11-08T18:00:00Z"],
  [9,"SF","LV",0.799,"2026-11-08T21:05:00Z"], [9,"LAC","HOU",0.5652,"2026-11-08T21:05:00Z"],
  [9,"NE","GB",0.5346,"2026-11-08T21:25:00Z"], [9,"SEA","ARI",0.8731,"2026-11-08T21:25:00Z"],
  [9,"CHI","TB",0.6446,"2026-11-09T01:20:00Z"], [9,"MIN","BUF",0.3756,"2026-11-10T01:15:00Z"],
  [10,"NYG","WAS",0.5224,"2026-11-13T01:15:00Z"], [10,"DET","NE",0.5385,"2026-11-15T14:30:00Z"],
  [10,"CLE","HOU",0.3489,"2026-11-15T18:00:00Z"], [10,"IND","MIA",0.753,"2026-11-15T18:00:00Z"],
  [10,"TEN","JAX",0.3775,"2026-11-15T18:00:00Z"], [10,"NYJ","BUF",0.2592,"2026-11-15T18:00:00Z"],
  [10,"ATL","KC",0.3554,"2026-11-15T18:00:00Z"], [10,"GB","MIN",0.6689,"2026-11-15T18:00:00Z"],
  [10,"NO","CAR",0.5747,"2026-11-15T18:00:00Z"], [10,"LV","SEA",0.2592,"2026-11-15T21:05:00Z"],
  [10,"ARI","LAR",0.1607,"2026-11-15T21:05:00Z"], [10,"DAL","SF",0.5347,"2026-11-15T21:25:00Z"],
  [10,"CIN","PIT",0.6746,"2026-11-16T01:20:00Z"], [10,"BAL","LAC",0.6049,"2026-11-17T01:15:00Z"],
  [11,"HOU","IND",0.669,"2026-11-20T01:15:00Z"], [11,"NYG","JAX",0.4888,"2026-11-22T18:00:00Z"],
  [11,"BUF","MIA",0.8731,"2026-11-22T18:00:00Z"], [11,"CAR","BAL",0.3483,"2026-11-22T18:00:00Z"],
  [11,"DAL","TEN",0.7574,"2026-11-22T18:00:00Z"], [11,"KC","ARI",0.8519,"2026-11-22T18:00:00Z"],
  [11,"CHI","NO",0.7219,"2026-11-22T18:00:00Z"], [11,"DET","TB",0.669,"2026-11-22T18:00:00Z"],
  [11,"LAC","NYJ",0.8236,"2026-11-22T21:05:00Z"], [11,"PHI","PIT",0.7297,"2026-11-22T21:25:00Z"],
  [11,"DEN","LV",0.799,"2026-11-22T21:25:00Z"], [11,"SF","MIN",0.6585,"2026-11-23T01:20:00Z"],
  [11,"WAS","CIN",0.4776,"2026-11-24T01:15:00Z"], [12,"LAR","GB",0.6842,"2026-11-26T01:00:00Z"],
  [12,"DET","CHI",0.5703,"2026-11-26T18:00:00Z"], [12,"DAL","PHI",0.5289,"2026-11-26T21:30:00Z"],
  [12,"BUF","KC",0.5994,"2026-11-27T01:20:00Z"], [12,"PIT","DEN",0.4673,"2026-11-27T20:00:00Z"],
  [12,"MIN","ATL",0.6165,"2026-11-29T18:00:00Z"], [12,"CLE","LV",0.5447,"2026-11-29T18:00:00Z"],
  [12,"IND","NYG",0.6049,"2026-11-29T18:00:00Z"], [12,"HOU","BAL",0.5112,"2026-11-29T18:00:00Z"],
  [12,"MIA","NYJ",0.5224,"2026-11-29T18:00:00Z"], [12,"CIN","NO",0.7297,"2026-11-29T18:00:00Z"],
  [12,"JAX","TEN",0.7113,"2026-11-29T21:05:00Z"], [12,"ARI","WAS",0.3483,"2026-11-29T21:25:00Z"],
  [12,"SF","SEA",0.5123,"2026-11-29T21:25:00Z"], [12,"LAC","NE",0.6049,"2026-11-30T01:20:00Z"],
  [12,"TB","CAR",0.6746,"2026-12-01T01:15:00Z"], [13,"LAR","KC",0.6852,"2026-12-04T01:15:00Z"],
  [13,"NO","GB",0.3489,"2026-12-06T18:00:00Z"], [13,"NYG","SF",0.3951,"2026-12-06T18:00:00Z"],
  [13,"CHI","JAX",0.6165,"2026-12-06T18:00:00Z"], [13,"ATL","DET",0.412,"2026-12-06T18:00:00Z"],
  [13,"CLE","CIN",0.3489,"2026-12-06T18:00:00Z"], [13,"TEN","WAS",0.4776,"2026-12-06T18:00:00Z"],
  [13,"TB","LAC",0.4788,"2026-12-06T18:00:00Z"], [13,"DEN","MIA",0.8161,"2026-12-06T21:05:00Z"],
  [13,"ARI","PHI",0.2233,"2026-12-06T21:05:00Z"], [13,"NE","BUF",0.4788,"2026-12-06T21:25:00Z"],
  [13,"MIN","CAR",0.6446,"2026-12-06T21:25:00Z"], [13,"PIT","HOU",0.4776,"2026-12-07T01:20:00Z"],
  [13,"SEA","DAL",0.6852,"2026-12-08T01:15:00Z"], [14,"NE","MIN",0.6951,"2026-12-11T01:15:00Z"],
  [14,"WAS","HOU",0.4776,"2026-12-13T18:00:00Z"], [14,"DET","TEN",0.7885,"2026-12-13T18:00:00Z"],
  [14,"PHI","IND",0.6951,"2026-12-13T18:00:00Z"], [14,"NYJ","DEN",0.3254,"2026-12-13T18:00:00Z"],
  [14,"CLE","ATL",0.4776,"2026-12-13T18:00:00Z"], [14,"MIA","CHI",0.3148,"2026-12-13T18:00:00Z"],
  [14,"BAL","TB",0.6951,"2026-12-13T18:00:00Z"], [14,"CAR","NO",0.5552,"2026-12-13T18:00:00Z"],
  [14,"LV","LAC",0.3049,"2026-12-13T21:05:00Z"], [14,"SEA","NYG",0.7807,"2026-12-13T21:25:00Z"],
  [14,"SF","LAR",0.4253,"2026-12-13T21:25:00Z"], [14,"CIN","KC",0.5112,"2026-12-13T21:25:00Z"],
  [14,"GB","BUF",0.5224,"2026-12-14T01:20:00Z"], [14,"JAX","PIT",0.6446,"2026-12-15T01:15:00Z"],
  [15,"LAC","SF",0.588,"2026-12-18T01:15:00Z"], [15,"PHI","SEA",0.5123,"2026-12-19T22:00:00Z"],
  [15,"BUF","CHI",0.6446,"2026-12-20T01:20:00Z"], [15,"HOU","JAX",0.6225,"2026-12-20T18:00:00Z"],
  [15,"TEN","IND",0.4788,"2026-12-20T18:00:00Z"], [15,"PIT","BAL",0.3951,"2026-12-20T18:00:00Z"],
  [15,"NYG","CLE",0.6852,"2026-12-20T18:00:00Z"], [15,"GB","MIA",0.8393,"2026-12-20T18:00:00Z"],
  [15,"CAR","CIN",0.3775,"2026-12-20T18:00:00Z"], [15,"TB","NO",0.6746,"2026-12-20T18:00:00Z"],
  [15,"WAS","ATL",0.6633,"2026-12-20T18:00:00Z"], [15,"ARI","NYJ",0.5224,"2026-12-20T21:05:00Z"],
  [15,"LV","DEN",0.3554,"2026-12-20T21:25:00Z"], [15,"LAR","DAL",0.7796,"2026-12-20T21:25:00Z"],
  [15,"MIN","DET",0.4448,"2026-12-21T01:20:00Z"], [15,"KC","NE",0.6165,"2026-12-22T01:15:00Z"],
  [16,"PHI","HOU",0.5551,"2026-12-25T01:15:00Z"], [16,"CHI","GB",0.5148,"2026-12-25T18:00:00Z"],
  [16,"DEN","BUF",0.4645,"2026-12-25T21:30:00Z"], [16,"SEA","LAR",0.4969,"2026-12-26T01:15:00Z"],
  [16,"IND","CIN",0.4776,"2026-12-27T05:00:00Z"], [16,"ATL","TB",0.4888,"2026-12-27T05:00:00Z"],
  [16,"PIT","CAR",0.6318,"2026-12-27T05:00:00Z"], [16,"MIN","WAS",0.5447,"2026-12-27T05:00:00Z"],
  [16,"BAL","CLE",0.8236,"2026-12-27T18:00:00Z"], [16,"MIA","LAC",0.2356,"2026-12-27T18:00:00Z"],
  [16,"NYJ","NE",0.2887,"2026-12-27T18:00:00Z"], [16,"NO","ARI",0.6951,"2026-12-27T18:00:00Z"],
  [16,"LV","TEN",0.5346,"2026-12-27T21:05:00Z"], [16,"KC","SF",0.6049,"2026-12-27T21:25:00Z"],
  [16,"DAL","JAX",0.6165,"2026-12-28T01:20:00Z"], [16,"DET","NYG",0.7113,"2026-12-29T01:15:00Z"],
  [17,"CIN","BAL",0.5212,"2027-01-01T01:15:00Z"], [17,"TB","LAR",0.3586,"2027-01-03T05:00:00Z"],
  [17,"NE","DEN",0.5747,"2027-01-03T05:00:00Z"], [17,"JAX","WAS",0.6049,"2027-01-03T05:00:00Z"],
  [17,"LAC","KC",0.5347,"2027-01-03T05:00:00Z"], [17,"DAL","NYG",0.6746,"2027-01-03T18:00:00Z"],
  [17,"NYJ","MIN",0.4253,"2027-01-03T18:00:00Z"], [17,"MIA","BUF",0.228,"2027-01-03T18:00:00Z"],
  [17,"ATL","NO",0.5652,"2027-01-03T18:00:00Z"], [17,"TEN","PIT",0.4888,"2027-01-03T18:00:00Z"],
  [17,"CAR","SEA",0.3049,"2027-01-03T18:00:00Z"], [17,"CLE","IND",0.4288,"2027-01-03T18:00:00Z"],
  [17,"ARI","LV",0.4888,"2027-01-03T21:05:00Z"], [17,"CHI","DET",0.5112,"2027-01-03T21:25:00Z"],
  [17,"SF","PHI",0.6225,"2027-01-04T01:20:00Z"], [17,"GB","HOU",0.6049,"2027-01-05T01:15:00Z"],
  [18,"CIN","CLE",0.7904,"2027-01-10T05:00:00Z"], [18,"KC","LV",0.8052,"2027-01-10T05:00:00Z"],
  [18,"LAR","SEA",0.633,"2027-01-10T05:00:00Z"], [18,"DEN","LAC",0.5286,"2027-01-10T05:00:00Z"],
  [18,"ARI","SF",0.2394,"2027-01-10T05:00:00Z"], [18,"BAL","PIT",0.7227,"2027-01-10T05:00:00Z"],
  [18,"CAR","ATL",0.5691,"2027-01-10T05:00:00Z"], [18,"BUF","NYJ",0.849,"2027-01-10T05:00:00Z"],
  [18,"NYG","PHI",0.4101,"2027-01-10T05:00:00Z"], [18,"IND","JAX",0.4989,"2027-01-10T05:00:00Z"],
  [18,"GB","DET",0.5618,"2027-01-10T05:00:00Z"], [18,"HOU","TEN",0.758,"2027-01-10T05:00:00Z"],
  [18,"NE","MIA",0.8209,"2027-01-10T05:00:00Z"], [18,"WAS","DAL",0.4883,"2027-01-10T05:00:00Z"],
  [18,"NO","TB",0.4772,"2027-01-10T05:00:00Z"], [18,"MIN","CHI",0.4823,"2027-01-10T05:00:00Z"],
];
// sd used when the board was built; keeps spread <-> probability round-trips
// consistent with the numbers above.
const MARGIN_SD = 10.81;
// Board uses NFL-standard abbreviations; teamMap may differ on a few.
const ABBR_ALIASES = { WAS: ['WSH', 'WAS'], WSH: ['WAS', 'WSH'], LAR: ['LAR', 'LA'], LV: ['LV', 'LVR'], JAX: ['JAX', 'JAC'] };

// Normal cdf / inverse cdf (Abramowitz-Stegun 7.1.26 and Acklam).
const normCdf = (z) => {
  const sg = z < 0 ? -1 : 1, x = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + 0.3275911 * x);
  const y = 1 - ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t
    - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);
  return 0.5 * (1 + sg * y);
};
const normInv = (p) => {
  const a = [-39.6968302866538, 220.946098424521, -275.928510446969,
             138.357751867269, -30.6647980661472, 2.50662827745924];
  const b = [-54.4760987982241, 161.585836858041, -155.698979859887,
             66.8013118877197, -13.2806815528857];
  const c = [-0.00778489400243029, -0.322396458041136, -2.40075827716184,
             -2.54973253934373, 4.37466414146497, 2.93816398269878];
  const d = [0.00778469570904146, 0.32246712907004, 2.445134137143, 3.75440866190742];
  const pl = 0.02425;
  if (p < pl) { const q = Math.sqrt(-2 * Math.log(p));
    return (((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) /
           ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1); }
  if (p > 1 - pl) { const q = Math.sqrt(-2 * Math.log(1 - p));
    return -(((((c[0]*q+c[1])*q+c[2])*q+c[3])*q+c[4])*q+c[5]) /
            ((((d[0]*q+d[1])*q+d[2])*q+d[3])*q+1); }
  const q = p - 0.5, r2 = q * q;
  return (((((a[0]*r2+a[1])*r2+a[2])*r2+a[3])*r2+a[4])*r2+a[5]) * q /
         (((((b[0]*r2+b[1])*r2+b[2])*r2+b[3])*r2+b[4])*r2+1);
};
const clampP = (p) => Math.min(Math.max(p, 0.01), 0.99);
const winProbFromMargin = (mu) => 1 - normCdf(-mu / MARGIN_SD);
const marginFromWinProb = (p) => normInv(clampP(p)) * MARGIN_SD;
// A team with n priced games keeps roughly n/(n+SHRINK_GAMES) of the revision
// the market implies; the rest of its board stays on the preseason number.
const SHRINK_GAMES = 8;

const CONTEST_FORMATS = {
  circa: { label: 'Circa' },                                        // Weeks 1–18 + TG + Xmas legs, 1 pick each (20 picks)
  splash: { label: 'Splash', doubleWeeks: [9, 12, 13, 14, 15, 16] }, // Weeks 1–18; 2 picks in weeks 9, 12–16 (24 picks)
};

// Smooth red→green gradient over the practical NFL win-prob range (20–80%);
// values outside clamp to the ends. The printed number carries precision —
// color is just for scanning.
const winColor = (p) => {
  if (p == null) return '#ffffff';
  const t = Math.max(0, Math.min(1, (p - 0.20) / 0.60));
  return `hsl(${Math.round(120 * t)}, 90%, 55%)`;
};

const customTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#141414', paper: '#141414' },
    text: { primary: '#ffffff' },
  },
  typography: { fontFamily: '"Roboto Mono", monospace' },
});

// start_date is UTC; shift to ET so US night games keep their US calendar day;
// reads on the shifted date use UTC getters.
const toEt = (d) => new Date(d.getTime() - 6 * 3600 * 1000);

// Circa holiday legs (Circa Survivor rules): Thanksgiving leg spans Wed–Fri
// around US Thanksgiving (4th Thursday of Nov); Christmas leg spans Dec 24–25.
const isTgLeg = (d) => {
  const e = toEt(d);
  if (e.getUTCMonth() !== 10) return false;
  // 4th Thursday of this November
  const first = new Date(Date.UTC(e.getUTCFullYear(), 10, 1));
  const firstThu = 1 + ((4 - first.getUTCDay() + 7) % 7);
  const tgDate = firstThu + 21;
  return e.getUTCDate() >= tgDate - 1 && e.getUTCDate() <= tgDate + 1;
};
const isXmasLeg = (d) => { const e = toEt(d); return e.getUTCMonth() === 11 && (e.getUTCDate() === 24 || e.getUTCDate() === 25); };

export default function Survivor() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { teamMap } = useLogoMaps();

  const [format, setFormat] = useState(() => localStorage.getItem('survivorFormat') || 'splash');
  useEffect(() => { localStorage.setItem('survivorFormat', format); }, [format]);

  const [pickMode, setPickMode] = useState(() => localStorage.getItem('survivorPickMode') || 'optimal');
  useEffect(() => { localStorage.setItem('survivorPickMode', pickMode); }, [pickMode]);

  // Custom picks: legKey → array of teamIds (seeded from optimal on first switch)
  const [customPicks, setCustomPicks] = useState(() => {
    try { return JSON.parse(localStorage.getItem('survivorCustomPicks')) || {}; } catch (e) { return {}; }
  });
  useEffect(() => { localStorage.setItem('survivorCustomPicks', JSON.stringify(customPicks)); }, [customPicks]);

  // Teams the user still has available (empty array = all available)
  const [unavailable, setUnavailable] = useState(() => {
    try { return JSON.parse(localStorage.getItem('survivorUnavailable')) || []; } catch (e) { return []; }
  });
  useEffect(() => { localStorage.setItem('survivorUnavailable', JSON.stringify(unavailable)); }, [unavailable]);

  // ── Throttled odds subscription ──
  const readBets = useCallback(() => {
    const data = dataStore.get('odds');
    const arr = data instanceof Map ? Array.from(data.values()) : (data || []);
    return arr.filter(b =>
      b && !b.pickem && b.league === 'NFL' && !b.isLive
      && (b.marketName === 'Moneyline' || b.betTypeDisplay === 'Moneyline')
      && b.marketCategory === 'Game Line'
    );
  }, []);
  const [bets, setBets] = useState(readBets);
  const timerRef = useRef(null);
  useEffect(() => {
    const unsub = dataStore.subscribe('odds', () => {
      if (timerRef.current) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setBets(readBets());
      }, THROTTLE_MS);
    });
    return () => { if (timerRef.current) clearTimeout(timerRef.current); unsub && unsub(); };
  }, [readBets]);

  // ── Build fixtures → legs → grid → optimal path ──
  const { teams, legs, grid, optimal } = useMemo(() => {
    const now = new Date();
    const fixtures = new Map();
    // Abbreviation -> teamId, so the hardcoded board can address live teams.
    // teamMap can carry more than one record per franchise, and picking the
    // wrong id splits a team into two rows: one fed by the board, one by the
    // odds feed. So ids seen in live bets win; teamMap is only the fallback.
    const liveIds = new Set();
    bets.forEach(b => {
      if (b?.awayTeamId) liveIds.add(b.awayTeamId);
      if (b?.homeTeamId) liveIds.add(b.homeTeamId);
    });
    const idByAbbr = {};
    Object.entries(teamMap || {}).forEach(([id, t]) => {
      const ab = t?.abbreviation?.toUpperCase();
      if (!ab) return;
      if (!idByAbbr[ab] || (liveIds.has(id) && !liveIds.has(idByAbbr[ab]))) {
        idByAbbr[ab] = id;
      }
    });
    const teamId = (abbr) => {
      const key = String(abbr).toUpperCase();
      for (const cand of (ABBR_ALIASES[key] || [key])) {
        if (idByAbbr[cand]) return idByAbbr[cand];
      }
      return idByAbbr[key] || null;
    };
    // An ordered (home, away) pair happens at most once a season, so it keys a
    // game across both sources without needing matching fixture ids.
    const gameKey = (homeId, awayId) => `${homeId}|${awayId}`;

    // Seed every remaining game from the board, priced or not.
    PRESEASON_BOARD.forEach(([wk, homeAb, awayAb, pHome, startIso]) => {
      const homeId = teamId(homeAb), awayId = teamId(awayAb);
      if (!homeId || !awayId) return;
      const start = new Date(startIso);
      if (!(start > now)) return;
      fixtures.set(gameKey(homeId, awayId), {
        start,
        awayTeamId: awayId, homeTeamId: homeId,
        books: new Map(),
        seasonWeek: wk,
        baseHome: pHome,        // preseason de-vigged P(home)
      });
    });

    bets.forEach(b => {
      if (!b.eventStart || !b.awayTeamId || !b.homeTeamId) return;
      const start = new Date(b.eventStart);
      if (start <= now) return; // past games (and finished weeks) drop off
      const key = gameKey(b.homeTeamId, b.awayTeamId);
      if (!fixtures.has(key)) {
        fixtures.set(key, {
          start,
          awayTeamId: b.awayTeamId, homeTeamId: b.homeTeamId,
          books: new Map(),       // bookName -> { away, home } raw prices
          seasonWeek: null,
          baseHome: null,
        });
      }
      const fx = fixtures.get(key);
      fx.start = start;           // live kickoff wins over the board's
      if (fx.seasonWeek == null) {
        const w = parseInt(b.seasonWeek ?? b.season_week, 10);
        if (Number.isFinite(w) && w > 0) fx.seasonWeek = w;
      }
      if (!b.bookName) return;
      if (!fx.books.has(b.bookName)) fx.books.set(b.bookName, { away: null, home: null });
      const bk = fx.books.get(b.bookName);
      if (b.side === 'Away') bk.away = b.price;
      else if (b.side === 'Home') bk.home = b.price;
    });

    if (fixtures.size === 0) return { teams: [], legs: [], grid: new Map(), optimal: new Map(), survival: null };

    const impliedProb = (price) => {
      const p = parseFloat(price);
      if (!p) return null;
      return p > 0 ? 100 / (p + 100) : -p / (-p + 100);
    };
    // De-vig one book's two-sided market into P(away wins).
    const devigAway = (bk) => {
      const a = impliedProb(bk.away);
      const h = impliedProb(bk.home);
      if (a == null || h == null || a + h <= 0) return null;
      return a / (a + h);
    };
    const median = (xs) => {
      const v = [...xs].sort((x, y) => x - y);
      const m = v.length >> 1;
      return v.length % 2 ? v[m] : (v[m - 1] + v[m]) / 2;
    };
    // Returns { win, src } where src names the book, or 'consensus (n)'.
    // Real market price for a game, or null. Null past the horizon, so a game
    // can never be trusted by the grid and distrusted by the revision fit (or
    // the reverse) — both callers go through here.
    const horizonMs = MARKET_HORIZON_DAYS * 24 * 3600 * 1000;
    const marketAwayWin = (fx) => {
      if (fx.start - now > horizonMs) return { win: null, src: null };
      for (const name of BOOK_PREFERENCE) {
        const bk = fx.books.get(name);
        if (!bk) continue;
        const p = devigAway(bk);
        if (p != null) return { win: p, src: name };
      }
      const others = [];
      fx.books.forEach(bk => {
        const p = devigAway(bk);
        if (p != null) others.push(p);
      });
      if (others.length >= MIN_CONSENSUS_BOOKS) {
        return { win: median(others), src: `consensus (${others.length})` };
      }
      // A one-sided quote is not a probability; let the board handle it.
      return { win: null, src: null };
    };

    // Week bucketing on the ET-shifted date, Tuesday boundary.
    // Anchored to the season's own Week 1 (the Tuesday after Labor Day, since
    // Week 1 kicks off the following Thursday) so leg numbers are true NFL
    // weeks and stay put as finished weeks roll off the grid.
    const starts = Array.from(fixtures.values()).map(f => f.start).sort((a, b) => a - b);
    const seasonAnchor = (d) => {
      const e = toEt(d);
      // Jan/Feb games belong to the prior year's season.
      const year = e.getUTCMonth() < 2 ? e.getUTCFullYear() - 1 : e.getUTCFullYear();
      const sept1 = new Date(Date.UTC(year, 8, 1));
      const laborDay = 1 + ((1 - sept1.getUTCDay() + 7) % 7); // first Monday of September
      return new Date(Date.UTC(year, 8, laborDay + 1));       // the Tuesday after it
    };
    const anchor = seasonAnchor(starts[0]);
    const weekFromDate = (d) => Math.floor((toEt(d) - anchor) / (7 * 24 * 3600 * 1000)) + 1;
    // Prefer the fixture's own season_week; fall back to date math.
    const weekOf = (fx) => fx.seasonWeek ?? weekFromDate(fx.start);

    const legSet = new Map();
    const doubleWeeks = new Set(CONTEST_FORMATS[format].doubleWeeks || []);
    const legOf = (fx) => {
      const d = fx.start;
      if (format === 'circa' && isTgLeg(d)) return { key: 'TG', label: 'TG', order: weekOf(fx) + 0.4, picks: 1 };
      if (format === 'circa' && isXmasLeg(d)) return { key: 'XMAS', label: 'Xmas', order: weekOf(fx) + 0.4, picks: 1 };
      const w = weekOf(fx);
      const picks = doubleWeeks.has(w) ? 2 : 1;
      return { key: `W${w}`, label: picks === 2 ? `${w} ×2` : `${w}`, order: w, picks };
    };

    // ── Market revisions to the preseason baseline ──────────────────────
    // For every game a book HAS priced, the gap between the posted margin and
    // the preseason margin is the market's revision on those two teams. Solve
    // (L + lambda*I) d = A'y for a per-team delta, ridged toward zero so teams
    // with little or no coverage keep their preseason rating instead of
    // absorbing a neighbour's move. L is the game graph Laplacian.
    const deltas = (() => {
      const ids = [];
      const ix = new Map();
      const obs = [];
      fixtures.forEach(fx => {
        if (fx.baseHome == null) return;              // not on the board
        const { win: pAway } = marketAwayWin(fx);
        if (pAway == null) return;                    // nothing trustworthy yet
        [fx.homeTeamId, fx.awayTeamId].forEach(id => {
          if (!ix.has(id)) { ix.set(id, ids.length); ids.push(id); }
        });
        // How far the market has moved on this game since preseason, in points.
        const posted = marginFromWinProb(1 - pAway);
        const baseline = marginFromWinProb(fx.baseHome);
        obs.push([ix.get(fx.homeTeamId), ix.get(fx.awayTeamId), posted - baseline]);
      });
      const n = ids.length;
      if (n === 0 || obs.length === 0) return {};
      const M = Array.from({ length: n }, () => new Float64Array(n + 1));
      for (let k = 0; k < n; k++) M[k][k] = SHRINK_GAMES;   // ridge
      obs.forEach(([h, a, y]) => {
        M[h][h] += 1; M[a][a] += 1; M[h][a] -= 1; M[a][h] -= 1;
        M[h][n] += y; M[a][n] -= y;
      });
      for (let c = 0; c < n; c++) {                        // gaussian elimination
        let piv = c;
        for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[piv][c])) piv = r;
        if (Math.abs(M[piv][c]) < 1e-12) continue;
        const t = M[c]; M[c] = M[piv]; M[piv] = t;
        for (let r = 0; r < n; r++) {
          if (r === c) continue;
          const f = M[r][c] / M[c][c];
          if (!f) continue;
          for (let k = c; k <= n; k++) M[r][k] -= f * M[c][k];
        }
      }
      const out = {};
      ids.forEach((id, k) => { out[id] = M[k][k] ? M[k][n] / M[k][k] : 0; });
      return out;
    })();

    // Preseason price for this game, shifted by what the market has since done
    // to these two teams elsewhere on the board.
    const modelAwayWin = (fx) => {
      if (fx.baseHome == null) return null;
      const shift = (deltas[fx.homeTeamId] || 0) - (deltas[fx.awayTeamId] || 0);
      return 1 - winProbFromMargin(marginFromWinProb(fx.baseHome) + shift);
    };

    const grid = new Map();
    const teamIds = new Set();
    fixtures.forEach(fx => {
      if (!fx.awayTeamId || !fx.homeTeamId) return;
      const leg = legOf(fx);
      if (!legSet.has(leg.key)) legSet.set(leg.key, leg);
      let { win: pAway, src } = marketAwayWin(fx);
      let modeled = false;
      if (pAway == null) {
        const mp = modelAwayWin(fx);
        if (mp != null) { pAway = mp; src = 'preseason board + market revisions'; modeled = true; }
      }
      teamIds.add(fx.awayTeamId); teamIds.add(fx.homeTeamId);
      if (!grid.has(fx.awayTeamId)) grid.set(fx.awayTeamId, new Map());
      if (!grid.has(fx.homeTeamId)) grid.set(fx.homeTeamId, new Map());
      grid.get(fx.awayTeamId).set(leg.key, { oppId: fx.homeTeamId, at: true, win: pAway, src, modeled, start: fx.start });
      grid.get(fx.homeTeamId).set(leg.key, { oppId: fx.awayTeamId, at: false, win: pAway != null ? 1 - pAway : null, src, modeled, start: fx.start });
    });

    const legs = Array.from(legSet.values()).sort((a, b) => a.order - b.order);
    const teams = Array.from(teamIds)
      .map(id => ({ id, abbr: teamMap[id]?.abbreviation || '?', name: teamMap[id]?.name || id, logo: teamMap[id]?.logo || '' }))
      .sort((a, b) => a.abbr.localeCompare(b.abbr));

    // Greedy optimal path: legs in order, best available unused team each leg
    const blocked = new Set(unavailable);
    const used = new Set();
    const optimal = new Map(); // legKey → teamId
    let survival = 1;
    let picks = 0;
    legs.forEach(leg => {
      const candidates = [];
      grid.forEach((cells, teamId) => {
        if (blocked.has(teamId) || used.has(teamId)) return;
        const c = cells.get(leg.key);
        if (c && c.win != null) candidates.push({ teamId, win: c.win });
      });
      candidates.sort((a, b) => b.win - a.win);
      const take = candidates.slice(0, leg.picks || 1);
      if (take.length) {
        optimal.set(leg.key, new Set(take.map(t => t.teamId)));
        take.forEach(t => { used.add(t.teamId); survival *= t.win; });
        picks += take.length;
      }
    });
    if (picks === 0) survival = null;

    return { teams, legs, grid, optimal, survival };
  }, [bets, teamMap, format, unavailable]);

  // Effective picks for display: optimal (computed) or the user's custom set.
  const { picksByLeg, effSurvival, incompleteLegs } = useMemo(() => {
    const picksByLeg = new Map();
    const incompleteLegs = new Set();
    let prod = 1, any = false;
    legs.forEach(leg => {
      let ids;
      if (pickMode === 'optimal') {
        ids = Array.from(optimal.get(leg.key) || []);
      } else {
        ids = (customPicks[leg.key] || []).filter(id => grid.get(id)?.get(leg.key)?.win != null && !unavailable.includes(id));
      }
      picksByLeg.set(leg.key, new Set(ids));
      if (ids.length < (leg.picks || 1)) incompleteLegs.add(leg.key);
      ids.forEach(id => {
        const w = grid.get(id)?.get(leg.key)?.win;
        if (w != null) { prod *= w; any = true; }
      });
    });
    return { picksByLeg, effSurvival: any ? prod : null, incompleteLegs };
  }, [legs, grid, optimal, pickMode, customPicks, unavailable]);

  // Custom-mode cell click: toggle pick; stealing a team from another leg
  // clears it there (that leg then shows as needing a pick).
  const handleCellClick = useCallback((legKey, teamId, legPickCap) => {
    if (pickMode !== 'custom') return;
    setCustomPicks(prev => {
      const next = {};
      Object.keys(prev).forEach(k => { next[k] = prev[k].filter(t => t !== teamId || k === legKey); });
      const arr = [...(next[legKey] || [])];
      const idx = arr.indexOf(teamId);
      if (idx >= 0) {
        arr.splice(idx, 1); // deselect
      } else {
        if (arr.length >= legPickCap) arr.shift(); // replace oldest when full
        arr.push(teamId);
      }
      next[legKey] = arr;
      return next;
    });
  }, [pickMode]);

  // Seed custom from optimal the first time the user switches over
  const handleModeChange = useCallback((mode) => {
    if (mode === 'custom' && Object.keys(customPicks).length === 0) {
      const seed = {};
      optimal.forEach((ids, legKey) => { seed[legKey] = Array.from(ids); });
      setCustomPicks(seed);
    }
    setPickMode(mode);
  }, [customPicks, optimal]);

  const cellSx = {
    border: '1px solid rgba(255,255,255,0.15)', padding: '4px 6px',
    textAlign: 'center', whiteSpace: 'nowrap', fontSize: '0.75rem', minWidth: 78,
    position: 'relative',
  };

  const teamOptions = teams;

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden', width: '100%' }}>
          <Box className="filter-section" sx={{ padding: isMobile ? '8px' : '16px', backgroundColor: 'background.default' }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems={isMobile ? 'stretch' : 'center'}>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 130 }}>
                <InputLabel id="survivor-format-label" shrink sx={{ color: '#ffffff' }}>Contest</InputLabel>
                <Select labelId="survivor-format-label" label="Contest" notched value={format} onChange={(e) => setFormat(e.target.value)}>
                  {Object.entries(CONTEST_FORMATS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="survivor-picks-label" shrink sx={{ color: '#ffffff' }}>Picks</InputLabel>
                <Select labelId="survivor-picks-label" label="Picks" notched value={pickMode} onChange={(e) => handleModeChange(e.target.value)}>
                  <MenuItem value="optimal">Optimal</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 170 }}>
                <InputLabel id="survivor-teams-label" shrink sx={{ color: '#ffffff' }}>Available Teams</InputLabel>
                <Select
                  labelId="survivor-teams-label" label="Available Teams" notched multiple
                  value={teamOptions.filter(t => !unavailable.includes(t.id)).map(t => t.id)}
                  onChange={(e) => {
                    const selected = new Set(e.target.value);
                    setUnavailable(teamOptions.filter(t => !selected.has(t.id)).map(t => t.id));
                  }}
                  renderValue={(sel) => sel.length === teamOptions.length ? 'All' : `${sel.length} of ${teamOptions.length}`}
                >
                  {teamOptions.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      <Checkbox checked={!unavailable.includes(t.id)} />
                      <ListItemText primary={t.abbr} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <Tooltip title="Chance of surviving every remaining leg with the highlighted picks — the product of their win probabilities. In Custom mode, click cells to set your own picks.">
                  <Typography sx={{ fontSize: '0.75rem', color: '#BBBBBB' }}>Survival Rate</Typography>
                </Tooltip>
                <Typography sx={{ fontSize: '1.1rem', color: effSurvival == null ? '#777777' : incompleteLegs.size > 0 ? '#ff9100' : '#00c853', fontWeight: 600 }}>
                  {effSurvival != null
                    ? `${(effSurvival * 100).toFixed(effSurvival >= 0.1 ? 1 : effSurvival >= 0.01 ? 2 : 3)}%${incompleteLegs.size > 0 ? ' *' : ''}`
                    : '—'}
                </Typography>
                {incompleteLegs.size > 0 && (
                  <Typography sx={{ fontSize: '0.65rem', color: '#ff9100' }}>
                    {incompleteLegs.size} week{incompleteLegs.size > 1 ? 's' : ''} need a pick
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', px: isMobile ? 1 : 2, pb: 2 }}>
            {teams.length === 0 ? (
              <Typography sx={{ color: '#BBBBBB', mt: 4, textAlign: 'center' }}>
                Waiting for NFL moneyline data…
              </Typography>
            ) : (
              <table style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...cellSx, position: 'sticky', left: 0, top: 0, zIndex: 3, backgroundColor: '#141414', textAlign: 'left', minWidth: 110 }}>Team</th>
                    {legs.map(leg => (
                      <th key={leg.key} style={{ ...cellSx, position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#141414', color: incompleteLegs.has(leg.key) ? '#ff9100' : '#ffffff' }}>
                        {leg.label}{incompleteLegs.has(leg.key) ? ' !' : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {teams.map(team => {
                    const isOut = unavailable.includes(team.id);
                    return (
                      <tr key={team.id} style={isOut ? { opacity: 0.35 } : undefined}>
                        <td style={{ ...cellSx, position: 'sticky', left: 0, zIndex: 1, backgroundColor: '#141414', textAlign: 'left' }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            {team.logo && <img src={team.logo} alt="" style={{ width: 18, height: 18 }} />}
                            <span>{team.abbr}</span>
                          </Stack>
                        </td>
                        {legs.map(leg => {
                          const cell = grid.get(team.id)?.get(leg.key);
                          if (!cell) {
                            return <td key={leg.key} style={{ ...cellSx, color: '#555555' }}>—</td>;
                          }
                          const opp = teamMap[cell.oppId]?.abbreviation || '?';
                          const pct = cell.win != null ? `${(cell.win * 100).toFixed(0)}%` : '—';
                          const isPick = picksByLeg.get(leg.key)?.has(team.id);
                          return (
                            <td key={leg.key}
                              onClick={() => handleCellClick(leg.key, team.id, leg.picks || 1)}
                              style={{
                                ...cellSx,
                                ...(isPick ? { outline: '2px solid #00e5ff', outlineOffset: '-2px' } : {}),
                                ...(pickMode === 'custom' && !isOut ? { cursor: 'pointer' } : {}),
                              }}>
                              <Tooltip title={cell.start.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}>
                                <span>
                                  <span style={{ color: '#BBBBBB' }}>{cell.at ? '@' : 'vs'} {opp}</span>
                                  <br />
                                  <span style={{ color: winColor(cell.win), fontWeight: 600, fontStyle: cell.modeled ? 'italic' : 'normal', opacity: cell.modeled ? 0.75 : 1 }}>{pct}</span>
                                </span>
                              </Tooltip>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Box>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
}